import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Textarea } from '@/components/UI/Textarea';
import { Alert } from '@/components/UI/Alert';
import { Badge } from '@/components/UI/Badge';
import { LoadingState } from '@/components/UI/LoadingState';
import { apiErrorMessage, getErrorDetail } from '@/utils/apiError';
import { layoutProblemText, layoutProblems } from './layoutProblems';
import {
  useGetResponseLayoutQuery,
  useUpdateResponseLayoutMutation,
  type ResponseLayoutSide,
} from '@/redux/internal-services/toolErrorsApi';

type Side = 'success' | 'error';
const SIDES: Side[] = ['success', 'error'];

const parse = (text: string): ResponseLayoutSide | null => {
  try {
    const v = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
};

const pretty = (v: unknown) => JSON.stringify(v ?? {}, null, 2);

/** Tool Responses -> Response layout: the default shape of every tool reply, per outcome. */
export const ResponseLayoutTab: React.FC = () => {
  const { t } = useTranslation();
  const query = useGetResponseLayoutQuery();
  const [save, { isLoading: saving }] = useUpdateResponseLayoutMutation();
  const layout = query.data?.data;
  const [text, setText] = useState<Record<Side, string>>({ success: '', error: '' });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Load the server copy into the editors only when it really changed (first load, a save): a refetch on
  // focus or a tag invalidation returns the same copy and must not throw away edits in progress.
  const loadedAt = useRef<string | null | undefined>(undefined);
  const loaded = useRef(false);
  useEffect(() => {
    if (!layout || (loaded.current && layout.edited_at === loadedAt.current)) return;
    loaded.current = true;
    loadedAt.current = layout.edited_at;
    setText({ success: pretty(layout.success), error: pretty(layout.error) });
  }, [layout]);


  if (query.isLoading) return <LoadingState label={t('platform.toolErrors.loading')} className="py-16" />;
  if (query.isError || !layout) {
    return <Alert tone="danger">{apiErrorMessage(query.error, t, t('platform.toolErrors.loadFailed'))}</Alert>;
  }

  const parsed = { success: parse(text.success), error: parse(text.error) };
  // The same check the server runs, so a missing required key shows while typing, not after a failed save.
  const problems = {
    success: layoutProblems(text.success, layout.required_keys.success),
    error: layoutProblems(text.error, layout.required_keys.error),
  };
  const valid = SIDES.every((s) => problems[s].length === 0);
  const dirty = SIDES.some((s) => text[s] !== pretty(layout[s]));

  const onSave = async () => {
    setError(null);
    setSaved(false);
    if (!parsed.success || !parsed.error || !valid) return;
    try {
      await save({ success: parsed.success, error: parsed.error }).unwrap();
      setSaved(true);
    } catch (e) {
      // The detail names the key the layout dropped; the message alone only says it is invalid.
      const detail = getErrorDetail(e);
      setError([apiErrorMessage(e, t, t('platform.toolErrors.saveFailed')), detail].filter(Boolean).join(' '));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-ui-text2">{t('platform.toolErrors.layoutIntro')}</p>
      <div className="flex flex-wrap items-center gap-2 text-caption text-ui-text2">
        <span>{t('platform.toolErrors.layoutPlaceholders')}</span>
        {layout.placeholders.map((p) => <Badge key={p} variant="neutral" className="font-mono">{`{{ ${p} }}`}</Badge>)}
        {(layout.keywords ?? []).map((k) => <Badge key={k} variant="accent" className="font-mono">{k}</Badge>)}
      </div>
      <p className="text-caption text-ui-text3">{t('platform.toolErrors.layoutOmitHint')}</p>
      {error && <Alert tone="danger">{error}</Alert>}
      {saved && !dirty && <Alert tone="success">{t('platform.toolErrors.layoutSaved')}</Alert>}

      <div className="grid gap-4 lg:grid-cols-2">
        {SIDES.map((side) => (
          <div key={side} className="card-surface space-y-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-h3 text-ui-text">
                {t(side === 'success' ? 'platform.toolErrors.onSuccess' : 'platform.toolErrors.onFailure')}
              </h3>
              <Badge variant={side === 'success' ? 'success' : 'accent'}>
                {t(side === 'success' ? 'platform.toolErrors.layoutSuccessCovers' : 'platform.toolErrors.layoutErrorCovers')}
              </Badge>
            </div>
            <Textarea
              label={t('platform.toolErrors.layoutJson')}
              className="font-mono text-xs"
              rows={9}
              value={text[side]}
              onChange={(e) => { setText({ ...text, [side]: e.target.value }); setSaved(false); }}
              error={problems[side].map((p) => layoutProblemText(p, t)).join(' ') || undefined}
            />
            <p className="text-caption text-ui-text3">
              {t('platform.toolErrors.layoutRequired')}{' '}
              <span className="font-mono">
                {Object.entries(layout.required_keys[side]).map(([k, v]) => `"${k}": "${v}"`).join(', ')}
              </span>
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => { setText({ success: pretty(layout.seed.success), error: pretty(layout.seed.error) }); setSaved(false); }}
        >
          {t('platform.toolErrors.layoutReset')}
        </Button>
        <Button variant="primary" onClick={onSave} disabled={!dirty || !valid} loading={saving}>
          {t('platform.toolErrors.save')}
        </Button>
      </div>
    </div>
  );
};
